/**
 * Template Service
 *
 * Fetches form-ready template data from the API for plan and workout editing.
 * Returns data structured to match the create/update endpoint schemas.
 */

import { apiClient } from './api/client';

import type { ID, SuccessApiResponse } from '../types';
import type {
  LibraryWorkoutListResponse,
  PastWorkoutListResponse,
} from '../types/api/template';
import type { ChallengeParticipantStatus } from '../types/domain/challenge.types';
import type { ChallengeOutput } from '../validation/challenge.schemas';
import type { CustomExerciseOutput } from '../validation/custom-exercise.schemas';
import type {
  PlanTemplateOutput,
  TemplateWorkoutOutput,
} from '../validation/template.schemas';

// =============================================================================
// TYPES
// =============================================================================

export type TemplateType = 'plan' | 'workout' | 'exercise' | 'challenge';
export type ImportableWorkoutSource = 'library' | 'past';

/**
 * API response type for plan template with required id
 */
export interface PlanTemplateResponse extends Omit<
  PlanTemplateOutput,
  'id' | 'workouts'
> {
  id: string;
  workouts: WorkoutTemplateResponse[];
}

/**
 * API response type for workout template with required id
 */
export interface WorkoutTemplateResponse extends Omit<
  TemplateWorkoutOutput,
  'id'
> {
  id: string;
}

/**
 * API response type for custom exercise template with required id
 */
export interface ExerciseTemplateResponse extends Omit<
  CustomExerciseOutput,
  'id'
> {
  id: string;
}

/**
 * API response type for custom challenge template with required id
 */
export interface ChallengeTemplateResponse extends Omit<
  ChallengeOutput,
  'id' | 'participants' | 'challengeable'
> {
  id: string;
  participants: {
    value: ID;
    label: string;
    avatar_url: string;
    status: ChallengeParticipantStatus;
    invited_at: string; // ISO 8601 date string
  }[];
  challengeable: {
    id: ID;
    [key: string]: Array<object | string | number> | string | number;
  };
}

/**
 * Discriminated union for template responses
 */
export type TemplateResponse<T extends TemplateType> = T extends 'plan'
  ? PlanTemplateResponse
  : T extends 'workout'
    ? WorkoutTemplateResponse
    : T extends 'exercise'
      ? ExerciseTemplateResponse
      : ChallengeTemplateResponse;

// =============================================================================
// SERVICE
// =============================================================================

export class TemplateService {
  readonly #baseUrl = '/template';

  /**
   * Fetch a form-ready template by type and ID
   *
   * @param type - Template type ('plan', 'workout', or 'exercise')
   * @param id - Template identifier
   * @returns Form-ready template data matching create/update schemas
   *
   * @example
   * ```typescript
   * const plan = await templateService.getTemplate('plan', 'abc123');
   * const workout = await templateService.getTemplate('workout', 'xyz789');
   * ```
   */
  async getTemplate<T extends TemplateType>(
    type: T,
    id: ID
  ): Promise<TemplateResponse<T>> {
    if (!id) {
      throw new Error('Template ID is required');
    }

    const response = await apiClient.get<
      SuccessApiResponse<TemplateResponse<T>>
    >(`${this.#baseUrl}/${type}/${id}`);

    return response.data.data;
  }

  /**
   * Fetch a plan template by ID
   * Convenience method with explicit return type
   */
  async getPlanTemplate(id: ID): Promise<PlanTemplateResponse> {
    return this.getTemplate('plan', id);
  }

  /**
   * Fetch a workout template by ID
   * Convenience method with explicit return type
   */
  async getWorkoutTemplate(id: ID): Promise<WorkoutTemplateResponse> {
    return this.getTemplate('workout', id);
  }

  /**
   * Fetch a custom exercise template by ID
   * Convenience method with explicit return type
   */
  async getExerciseTemplate(id: ID): Promise<ExerciseTemplateResponse> {
    return this.getTemplate('exercise', id);
  }

  /**
   * Fetch a challenge template by ID
   * Convenience method with explicit return type
   */
  async getChallengeTemplate(id: ID): Promise<ChallengeTemplateResponse> {
    return this.getTemplate('challenge', id);
  }

  /**
   * Fetch importable workouts by source type
   *
   * @param type - Source type ('library' for curated templates, 'past' for completed workouts)
   * @returns List of importable workouts with metadata
   *
   * @example
   * ```typescript
   * const libraryWorkouts = await templateService.getImportableWorkouts('library');
   * const pastWorkouts = await templateService.getImportableWorkouts('past');
   * ```
   */
  async getImportableWorkouts<T extends ImportableWorkoutSource>(
    type: T
  ): Promise<
    T extends 'library' ? LibraryWorkoutListResponse : PastWorkoutListResponse
  > {
    // API returns LibraryWorkoutListResponse/PastWorkoutListResponse directly
    // (not wrapped in SuccessApiResponse)
    const response = await apiClient.get<
      T extends 'library' ? LibraryWorkoutListResponse : PastWorkoutListResponse
    >(`${this.#baseUrl}/importable`, { params: { type } });

    return response.data;
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

export const templateService = new TemplateService();
